import { Link } from 'react-router-dom';
import { useSeo, buildBreadcrumbSchema } from '@/hooks/useSeo';
import { contentPrinciples, aboutVoices, milestones, siteStats } from '@/mocks/siteContent';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

export default function About() {
  useSeo({
    title: '关于我们 - 看瓜爆料 | 看瓜爆料合集汇总专题',
    description:
      '看瓜爆料是一个把娱乐瓜料按事件与时间线打包成合集的专题站。这里介绍我们的品牌定位、发展理念、内容原则与联系方式，欢迎理性吃瓜。',
    keywords: '看瓜爆料,看瓜爆料合集,关于看瓜爆料,看瓜爆料专题,内容原则',
    canonical: '/about',
  });

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于看瓜爆料',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'Organization',
      name: '看瓜爆料',
      description: '把娱乐瓜料按事件、人物与时间线打包成合集专题的内容站点。',
      email: 'hello@kanguabaoliao.site',
      knowsAbout: ['娱乐合集整理', '事件时间线', '内容聚合'],
    },
  };

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: '首页', path: '/' },
    { name: '关于我们', path: '/about' },
  ]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-10 py-6 md:py-8">
          <Breadcrumbs items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />
        </div>
      </div>

      <section className="w-full px-4 md:px-10 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="text-xs md:text-sm label-font text-primary-600 tracking-[0.2em]">
              ABOUT US
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight">
              关于看瓜爆料
            </h1>
            <p className="mt-5 text-[15px] md:text-base text-foreground-700 leading-relaxed">
              看瓜爆料是一个专注「合集汇总」的娱乐专题站。我们不追逐单条热搜，
              而是把同一件事在不同时间点散落的公开信息收集起来，按事件重新排好顺序，
              做成一条可以完整读完的时间线。
            </p>
            <p className="mt-4 text-[15px] md:text-base text-foreground-600 leading-relaxed">
              你不需要在多个平台之间来回切换，也不用担心只看了一半就被新的信息打断。
              打开一个专题，就能从起因一路看到后续。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/collections"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm label-font hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-book-shelf-line"></i>
                去合集库看看
              </Link>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-foreground-300 text-foreground-800 text-sm label-font hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                联系我们
              </a>
            </div>
          </div>

          <div className="w-full h-64 md:h-96 rounded-4xl overflow-hidden border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20editorial%20desk%20scene%20with%20notebook%2C%20magnifying%20glass%20and%20stacked%20timeline%20cards%20on%20a%20cream%20surface%2C%20soft%20amber%20light%2C%20stylized%20minimal%20still%20life%2C%20muted%20coral%20and%20beige%20palette%2C%20high%20detail&width=900&height=700&seq=guagua-about-01&orientation=landscape"
              alt="看瓜爆料 内容整理工作场景示意"
              title="看瓜爆料 合集整理"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section id="brand" className="w-full px-4 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                我们做什么，为什么这样做
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                娱乐话题的特点是「更新快、信息碎」。一条爆料刚出来，往往还要等好几轮回应才能拼出全貌。
                大多数人没有时间追完整条链路，于是就有了「看完还是不明白」的挫败感。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                看瓜爆料解决的就是这个问题：把碎片重新组织成结构。每个专题都有明确的起止时间、
                编号节点和最近更新时间，你可以一眼看到自己错过了哪一段。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {siteStats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-5 rounded-lg bg-background-50 border border-background-200 text-center"
                >
                  <div className="font-heading font-bold text-2xl md:text-3xl text-primary-600">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-foreground-600 label-font">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            我们的发展脉络
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {milestones.map((item, index) => (
              <div
                key={item.title}
                className="relative p-5 rounded-lg bg-background-50 border border-background-200"
              >
                <span className="inline-block px-2 py-0.5 rounded-md bg-accent-100 text-accent-900 text-xs label-font">
                  {item.date}
                </span>
                <h3 className="mt-3 font-heading font-bold text-base text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                <span className="absolute right-4 bottom-3 font-heading font-black text-3xl text-background-200 select-none">
                  0{index + 1}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="principles" className="w-full px-4 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs md:text-sm label-font text-secondary-600 tracking-[0.2em]">
              PRINCIPLES
            </span>
            <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              内容原则
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              吃瓜也可以理性、有边界。以下四条是我们整理每一个专题时都会遵循的标准。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {contentPrinciples.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200"
              >
                <span className="shrink-0 w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            来自读者的口碑
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {aboutVoices.map((item) => (
              <figure
                key={item.name}
                className="p-5 md:p-6 rounded-lg border border-background-200 bg-background-50"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 font-heading font-bold">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="text-sm label-font text-foreground-800">
                    {item.name}
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full px-4 md:px-10 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto rounded-4xl bg-foreground-950 text-background-50 px-6 md:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50">
                联系方式
              </h2>
              <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed">
                如果你发现专题中的信息有误，或者希望我们优先整理某个事件的时间线，
                欢迎随时来信。我们会认真阅读每一条反馈。
              </p>
              <ul className="mt-6 space-y-3 text-sm label-font">
                <li className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/10 text-accent-400">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  hello@kanguabaoliao.site
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/10 text-accent-400">
                    <i className="ri-edit-2-line text-lg"></i>
                  </span>
                  内容纠错与选题建议
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/10 text-accent-400">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  工作日 48 小时内回复
                </li>
              </ul>
            </div>
            <div className="lg:pl-10 lg:border-l lg:border-background-50/15">
              <h3 className="font-heading font-bold text-lg text-background-50">
                订阅合集更新
              </h3>
              <p className="mt-2 text-sm text-background-200 leading-relaxed">
                每周一封邮件，汇总新增与重点更新的专题，随时可取消。
              </p>
              <a
                href="#newsletter-form"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-accent-950 text-sm label-font hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                前往订阅
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}